import { createSlice, createSelector } from "@reduxjs/toolkit";

const initialState = {
  items: [],
  byId: {},
  lastSyncAt: null,
};

function normalizeStatus(s) {
  return String(s || "").toUpperCase();
}

function extractListPayload(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

function adaptTransfer(t) {
  return {
    id: t?.id ?? null,
    exchange: t?.exchange ?? null,

    payer_type: t?.payer_type ?? null,
    payer_user: t?.payer_user ?? null,
    payee_type: t?.payee_type ?? null,
    payee_user: t?.payee_user ?? null,

    currency: t?.currency ?? null,
    amount: t?.amount ?? null,

    related_transaction: t?.related_transaction ?? null,

    status: normalizeStatus(t?.status),
    payer_sent_confirmed: !!t?.payer_sent_confirmed,
    payer_sent_confirmed_at: t?.payer_sent_confirmed_at ?? null,
    payee_received_confirmed: !!t?.payee_received_confirmed,
    payee_received_confirmed_at: t?.payee_received_confirmed_at ?? null,

    created_at: t?.created_at ?? null,

    raw: t,
  };
}

function adaptTx(tx) {
  const pair = tx?.pair || null;

  const fromCur = pair?.currency_from || "—";
  const toCur = pair?.currency_to || "—";
  const fromMethod = pair?.method_from?.title || pair?.method_from?.code || "—";
  const toMethod = pair?.method_to?.title || pair?.method_to?.code || "—";

  const rawTransfers = Array.isArray(tx?.transfers) ? tx.transfers : [];
  const transfers = rawTransfers.map(adaptTransfer).filter((x) => !!x.id);

  return {
    id: tx?.id,
    exchange: tx?.exchange ?? null,

    status: normalizeStatus(tx?.status),
    code: tx?.code || "—",
    created_at: tx?.created_at || null,
    updated_at: tx?.updated_at || null,

    amount_from: tx?.amount_from,
    amount_to: tx?.amount_to,

    from: {currency: fromCur, method: fromMethod},
    to: {currency: toCur, method: toMethod},

    payout_detail: tx?.payout?.detail || "—",
    payout_type: tx?.payout?.type || "—",

    buy_rate: tx?.buy_rate ?? null,
    sell_rate: tx?.sell_rate ?? null,

    transfers,
    transfers_by_id: transfers.reduce((acc, t) => {
      acc[t.id] = t;
      return acc;
    }, {}),

    raw: tx,
  };
}

// Map WS filter value → status values it covers
const FILTER_STATUSES = {
  active:    ["CREATED", "IN_PROGRESS"],
  completed: ["COMPLETED"],
  dispute:   ["DISPUTE"],
  cancelled: ["CANCELLED"],
};

const exchangeActiveSlice = createSlice({
  name: "exchangeActive",
  initialState,
  reducers: {
    upsertFromApi(state, action) {
      const raw = extractListPayload(action.payload);

      const items = raw.map(adaptTx).filter((x) => !!x.id);

      const byId = {};
      for (const it of items) byId[it.id] = it;

      state.items = items;
      state.byId = byId;
      state.lastSyncAt = new Date().toISOString();
    },

    /**
     * Merge results for a single WS filter without wiping other filters.
     * payload: { filter: "active"|"completed"|"dispute"|"cancelled", results: [...raw api items] }
     */
    upsertFiltered(state, action) {
      const { filter, results } = action.payload || {};
      const statuses = FILTER_STATUSES[filter];
      if (!statuses) return; // unknown filter — ignore

      const incoming = extractListPayload(results)
        .map(adaptTx)
        .filter((x) => !!x.id);

      // Remove existing items that belong to this filter's status group
      const kept = state.items.filter(
        (x) => !statuses.includes(normalizeStatus(x.status))
      );

      // Merge: kept items + incoming
      const merged = [...kept, ...incoming];

      const byId = {};
      for (const it of merged) byId[it.id] = it;

      state.items = merged;
      state.byId = byId;
      state.lastSyncAt = new Date().toISOString();
    },

    clear(state) {
      state.items = [];
      state.byId = {};
      state.lastSyncAt = null;
    },
  },
});

export const {upsertFromApi, upsertFiltered, clear} = exchangeActiveSlice.actions;

// ── Base selectors (primitives — no memoization needed) ─────────────────────
export const selectAllExchanges    = (s) => s.exchangeActive.items;
export const selectExchangesCount  = (s) => s.exchangeActive.items.length;
export const selectExchangeLastSyncAt = (s) => s.exchangeActive.lastSyncAt;
export const selectExchangeById    = (id) => (s) => (id ? s.exchangeActive.byId[id] : null);

// ── Derived selectors (memoized — return new arrays, must use createSelector) ─
export const selectActiveExchanges = createSelector(
  selectAllExchanges,
  (items) => items.filter((x) => x.status === "CREATED" || x.status === "IN_PROGRESS")
);

export const selectActiveCount = createSelector(
  selectActiveExchanges,
  (items) => items.length
);

export const selectCompletedExchanges = createSelector(
  selectAllExchanges,
  (items) => items.filter((x) => x.status === "COMPLETED")
);

export const selectDisputeExchanges = createSelector(
  selectAllExchanges,
  (items) => items.filter((x) => x.status === "DISPUTE")
);

export const selectCancelledExchanges = createSelector(
  selectAllExchanges,
  (items) => items.filter((x) => x.status === "CANCELLED")
);

export default exchangeActiveSlice.reducer;